import { resolveDeviceOnline, resolveDeviceStatus, resolveScreenState, resolvePlaybackDetail, resolvePlayback } from '../../utils';
import { formatWallClock12h, arabicDeltaPhrase } from '../../utils/timezone';
import { formatRelativeArabic } from '../../utils/datetime';
import { compTone, chipText, effectiveSound, resolveMuted, formatUptime } from './Component/Health/model';

/**
 * Screens LIST view -- the ONE state line / dot / facts of a screen row, from the SAME resolvers the card uses (resolveDeviceStatus,
 * resolveScreenState, resolvePlaybackDetail, the health entry's components, resolveMuted, the backend's clock-drift fields). No second copy of any
 * status rule lives here: this file only PRESENTS those answers as the prototype's single line.
 *
 * The card passes its live values (the optimistic play/pause guess, the realtime mute); the page-level callers (group summary) pass none and get the
 * REST-only answer.
 */
export const MAX_CONTENT_RETRY_ATTEMPTS = 5;

/** sound problems that are NOT "muted" and not fine: the speaker output is the problem */
const SOUND_OUTPUT_WORDS = ['output_stalled', 'external_fault'];

export function resolveRow({ data, healthEntry, isPlaying, rtdbMuted } = {}) {
  const d = data || {};
  const status = resolveDeviceStatus(d.connectivity, d.presence_status, d.online);
  const online = resolveDeviceOnline(d.connectivity, d.online);
  const playing = isPlaying !== undefined ? isPlaying : resolvePlayback(d.connectivity);
  const screenState = status === 'online' ? resolveScreenState(d.connectivity, d.presence_status, d.online, playing, healthEntry?.state) : status;
  const detail = resolvePlaybackDetail(d.connectivity);
  const audio = d.connectivity?.audio_health?.state;
  const muted = resolveMuted(rtdbMuted, healthEntry?.muted) === true;
  const comps = healthEntry?.components
    ? { internet: healthEntry.components.internet, sound: effectiveSound(healthEntry.components.sound, muted), picture: healthEntry.components.picture }
    : null;
  const hasRealTz = Boolean(d.branch?.timezone || d.company?.timezone);
  const clockShown = online && hasRealTz && !!formatWallClock12h(d.device_local_time);
  const clockBehind = clockShown && d.timezone_mismatch === true;
  const retrying = (d.content_corrupted_attempts ?? 0) < MAX_CONTENT_RETRY_ATTEMPTS;

  // The ONE issue, in priority order. `code` picks the wording; sev decides the stripe/colour.
  let issue = null;
  if (status === 'offline') issue = { sev: 'crit', code: 'offline' };
  else if (status === 'app_closed') issue = { sev: 'warn', code: 'app_closed' };
  else if (status === 'content_corrupted') issue = { sev: 'warn', code: retrying ? 'content_retrying' : 'content_exhausted' };
  else if (status === 'online') {
    if (detail === 'external_fault' || audio === 'external_fault' || audio === 'failed' || detail === 'no_sound'
      || (comps && SOUND_OUTPUT_WORDS.includes(comps.sound?.word))) issue = { sev: 'warn', code: 'no_sound' };
    else if (audio === 'repairing') issue = { sev: 'warn', code: 'audio_repairing' };
    else if (detail === 'media_stalled') issue = { sev: 'warn', code: 'media_stalled' };
    else if (comps && comps.sound?.state === 'warn' && comps.sound?.word === 'muted') issue = { sev: 'warn', code: 'muted' };
    else if (comps && (compTone(comps.sound) === 'c' || compTone(comps.internet) === 'c' || compTone(comps.picture) === 'c')) {
      const bad = ['internet', 'sound', 'picture'].find((n) => compTone(comps[n]) === 'c');
      issue = { sev: 'warn', code: 'component', comp: bad };
    } else if (clockBehind) issue = { sev: 'warn', code: 'clock' };
  }
  const sev = issue ? issue.sev : 'ok';
  const neutral = !issue && (status === 'standby' || status === 'scheduled_off' || screenState === 'paused'
    || (status === 'online' && (detail === 'loading' || detail === 'prayer')));
  const dot = sev === 'crit' ? 'off' : sev === 'warn' ? 'warn' : neutral ? 'pause' : 'ok';
  return { status, online, screenState, playing, detail, muted, comps, clockShown, clockBehind, issue, sev, dot, neutral, retrying };
}

/** the prototype's single state line: { cls: ok|warn|crit, icon: ''|off|warn|pause, text } */
export function rowStateLine(row, t, { data, uptimeText } = {}) {
  const d = data || {};
  const { issue } = row;
  if (issue) {
    switch (issue.code) {
      case 'offline': return { cls: 'crit', icon: 'off', text: t('screens_list.offline_seen', { time: formatRelativeArabic(d.connectivity?.last_seen_at || d.last_login || d.created_at) }) };
      case 'app_closed': return { cls: 'warn', icon: 'warn', text: t('screens_list.issue_app_closed') };
      case 'clock': return { cls: 'warn', icon: 'warn', text: t('screens_list.issue_clock', { dir: t((d.offset_delta_minutes ?? 0) > 0 ? 'screens_list.clock_ahead' : 'screens_list.clock_behind'), amount: arabicDeltaPhrase(Math.abs(d.offset_delta_minutes ?? 0)) }) };
      case 'muted': return { cls: 'warn', icon: 'warn', text: t('screens_list.issue_muted') };
      case 'no_sound': return { cls: 'warn', icon: 'warn', text: t('screens_list.issue_no_sound') };
      case 'audio_repairing': return { cls: 'warn', icon: 'warn', text: t('devices.appstate_audio_repairing') };
      case 'media_stalled': return { cls: 'warn', icon: 'warn', text: t('devices.appstate_media_stalled') };
      case 'content_retrying': return { cls: 'warn', icon: 'warn', text: t('devices.content_retrying_chip') };
      case 'content_exhausted': return { cls: 'warn', icon: 'warn', text: t('devices.content_exhausted_chip') };
      case 'component': return { cls: 'warn', icon: 'warn', text: chipText(t, issue.comp, row.comps[issue.comp]) };
      default: break;
    }
  }
  if (row.status === 'standby') return { cls: 'ok', icon: 'pause', text: t('status.standby') };
  if (row.status === 'scheduled_off') return { cls: 'ok', icon: 'pause', text: t('status.scheduled_off') };
  if (row.screenState === 'paused') return { cls: 'ok', icon: 'pause', text: t('screens_list.paused') };
  if (row.status === 'online' && row.detail === 'loading') return { cls: 'ok', icon: '', text: t('devices.appstate_loading') };
  if (row.status === 'online' && row.detail === 'prayer') return { cls: 'ok', icon: '', text: t('devices.appstate_prayer') };
  return { cls: 'ok', icon: '', text: uptimeText ? t('screens_list.connected_since', { up: uptimeText }) : t('status.online') };
}

/** the «now» cell: kind = none | off | playing | paused */
export function rowNow(row, label) {
  if (row.status === 'offline') return { kind: 'off', label: '' };
  if (row.status === 'online' && row.playing === true && label) return { kind: 'playing', label };
  if (row.status === 'online' && row.playing === false && label) return { kind: 'paused', label };
  return { kind: 'none', label: '' };
}

/** the detail strip: [{ cls: ''|ok|warn|crit, icon: ''|check|off, text }] -- never claims a component the health feed has not reported */
export function rowFacts(row, t, { data, uptimeText } = {}) {
  const d = data || {};
  const facts = [];
  if (row.status === 'offline') {
    facts.push({ cls: 'crit', icon: 'off', text: t('screens_list.fact_internet_off') });
    facts.push({ cls: '', icon: '', text: t('screens_list.fact_seen', { time: formatRelativeArabic(d.connectivity?.last_seen_at || d.last_login || d.created_at) }) });
    facts.push({ cls: '', icon: '', text: t('screens_list.fact_onsite') });
    return facts;
  }
  const closed = row.status === 'app_closed';
  facts.push(closed ? { cls: 'warn', icon: '', text: t('screens_list.fact_app_closed') } : { cls: 'ok', icon: 'check', text: t('screens_list.fact_app_ok') });
  const c = row.comps;
  if (c) {
    facts.push(compTone(c.internet) === 'g' ? { cls: 'ok', icon: 'check', text: t('sh.internet') } : { cls: toneCls(c.internet), icon: '', text: chipText(t, 'internet', c.internet) });
    if (compTone(c.sound) === 'g') facts.push({ cls: 'ok', icon: 'check', text: t('sh.sound') });
    else if (c.sound?.word === 'muted') facts.push({ cls: 'warn', icon: '', text: t('screens_list.issue_muted') });
    else facts.push({ cls: toneCls(c.sound), icon: '', text: chipText(t, 'sound', c.sound) });
    facts.push(compTone(c.picture) === 'g' ? { cls: 'ok', icon: 'check', text: t('sh.picture') } : { cls: toneCls(c.picture), icon: '', text: chipText(t, 'picture', c.picture) });
  }
  const clock = formatWallClock12h(d.device_local_time);
  if (row.clockShown && clock) facts.push({ cls: '', icon: '', text: t('screens_list.fact_clock', { time: clock }) });
  if (uptimeText) facts.push({ cls: '', icon: '', text: t('screens_list.connected_since', { up: uptimeText }) });
  return facts;
}
const toneCls = (comp) => ({ g: 'ok', c: 'crit', w: 'warn' }[compTone(comp)] || '');

/** group header summary for the screens the group holds: { off, warn } (the prototype's «N غير متصلة» / «N تحتاج انتباهًا») */
export function groupSummary(items, healthById, reported) {
  let off = 0; let warn = 0;
  (items || []).forEach((d) => {
    // a row that is on screen reports its OWN resolved state (it sees the realtime mute / optimistic play); a screen not drawn right now falls back to the REST-only answer
    const r = reported?.[d.id] || (() => { const x = resolveRow({ data: d, healthEntry: healthById?.[d.id] }); return { off: x.status === 'offline', warn: x.status !== 'offline' && x.sev === 'warn' }; })();
    if (r.off) off += 1;
    else if (r.warn) warn += 1;
  });
  return { off, warn };
}

export { formatUptime };
